import { useState, useEffect, useRef } from 'react'
import { X, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import recibosService from '../../services/recibosService'
import clientesService from '../../services/clientesService'
import { contem } from '../../utils/texto'

export default function NovoReciboModal({ orgId, leiloes, leilaoIdInicial, recibo = null, onClose, onSaved }) {
  const editando = !!recibo
  // Pré-seleciona o leilão mais recente ativo (ou o do recibo, ao editar)
  const leilaoInicial = editando
    ? (leiloes.find(l => String(l.id) === String(recibo.leilao_id)) || (recibo.leilao_id ? { id: recibo.leilao_id, nome: recibo.leilao_nome || '' } : null))
    : leilaoIdInicial
    ? leiloes.find(l => String(l.id) === String(leilaoIdInicial))
    : leiloes[0] || null

  const [form, setForm] = useState({
    data: editando ? (recibo.data || '') : new Date().toISOString().split('T')[0],
    tipo: editando ? recibo.tipo : 'recebimento',
    valor: editando ? String(recibo.valor ?? '').replace('.', ',') : '',
    descricao: editando ? (recibo.descricao || '') : '',
    cliente_id: editando && recibo.cliente_id ? String(recibo.cliente_id) : '',
    cliente_nome_avulso: editando ? (recibo.cliente_nome_avulso || '') : '',
    leilao_id: leilaoInicial ? String(leilaoInicial.id) : '',
  })
  const [clientes, setClientes] = useState([])
  const [searchCliente, setSearchCliente] = useState(editando && recibo.cliente_id ? (recibo.cliente_nome || '') : '')
  const [showDropdown, setShowDropdown] = useState(false)
  const [loadingClientes, setLoadingClientes] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchLeilao, setSearchLeilao] = useState(leilaoInicial?.nome || '')
  const [showLeilaoDropdown, setShowLeilaoDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const leilaoDropdownRef = useRef(null)

  // Busca clientes
  useEffect(() => {
    if (!orgId) return
    setLoadingClientes(true)
    clientesService.getByOrganizador(orgId, 1, 1000)
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.data || [])
        setClientes(list.sort((a, b) => (a.nome || '').localeCompare(b.nome || '')))
      })
      .catch(() => setClientes([]))
      .finally(() => setLoadingClientes(false))
  }, [orgId])

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
      if (leilaoDropdownRef.current && !leilaoDropdownRef.current.contains(e.target)) {
        setShowLeilaoDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredClientes = clientes.filter(c =>
    contem((c.nome || ''), searchCliente.toLowerCase()) ||
    (c.cpf_cnpj || '').includes(searchCliente)
  ).slice(0, 15)

  const selectCliente = (c) => {
    setForm({ ...form, cliente_id: String(c.id), cliente_nome_avulso: '' })
    setSearchCliente(c.nome)
    setShowDropdown(false)
  }

  const clearCliente = () => {
    setForm({ ...form, cliente_id: '' })
    setSearchCliente('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.valor || parseFloat(form.valor.replace(',', '.')) <= 0) {
      toast.error('Informe um valor válido')
      return
    }
    setSaving(true)
    try {
      const payload = {
        data: form.data,
        tipo: form.tipo,
        valor: parseFloat(form.valor.replace(',', '.')),
        descricao: form.descricao || null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        leilao_id: form.leilao_id ? parseInt(form.leilao_id) : null,
        cliente_nome_avulso: !form.cliente_id ? (form.cliente_nome_avulso || null) : null,
      }
      if (editando) await recibosService.update(orgId, recibo.id, payload)
      else await recibosService.create(orgId, payload)
      onSaved()
    } catch (err) {
      toast.error('Erro ao salvar recibo: ' + (err.response?.data?.detail || err.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">{editando ? `Editar recibo${recibo.numero ? ` nº ${recibo.numero}` : ""}` : "Cadastro de recibo"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Linha 1: Data, Tipo, Valor */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Data</label>
              <input
                type="date"
                value={form.data}
                onChange={e => setForm({ ...form, data: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Tipo</label>
              <select
                value={form.tipo}
                onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="recebimento">Recebimento</option>
                <option value="pagamento">Pagamento</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">Valor</label>
              <input
                type="text"
                value={form.valor}
                onChange={e => setForm({ ...form, valor: e.target.value })}
                placeholder="0,00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Descrição</label>
            <input
              type="text"
              value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })}
              placeholder="Ex: Recebimento de comissão"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Cliente com busca */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Cliente</label>
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchCliente}
                  onChange={e => {
                    setSearchCliente(e.target.value)
                    setShowDropdown(true)
                    if (!e.target.value) clearCliente()
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Digite para buscar cliente ou deixe vazio..."
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchCliente && (
                  <button type="button" onClick={clearCliente}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {/* Dropdown de resultados */}
              {showDropdown && searchCliente && filteredClientes.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredClientes.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => selectCliente(c)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                    >
                      <span className="font-medium">{c.nome}</span>
                      {c.cpf_cnpj && <span className="text-gray-400 ml-2 text-xs">{c.cpf_cnpj}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {form.cliente_id && (
              <p className="text-xs text-green-600 mt-1">✓ Cliente selecionado</p>
            )}
          </div>

          {/* Nome avulso (quando não seleciona cliente) */}
          {!form.cliente_id && (
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">
                Nome avulso <span className="text-gray-400">(opcional, se não selecionou cliente)</span>
              </label>
              <input
                type="text"
                value={form.cliente_nome_avulso}
                onChange={e => setForm({ ...form, cliente_nome_avulso: e.target.value })}
                placeholder="Nome para constar no recibo"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Leilão com busca */}
          <div>
            <label className="block text-xs text-gray-500 font-medium mb-1">Leilão</label>
            <div className="relative" ref={leilaoDropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchLeilao}
                  onChange={e => {
                    setSearchLeilao(e.target.value)
                    setShowLeilaoDropdown(true)
                    if (!e.target.value) setForm({ ...form, leilao_id: '' })
                  }}
                  onFocus={() => setShowLeilaoDropdown(true)}
                  placeholder="Digite para buscar leilão..."
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchLeilao && (
                  <button type="button" onClick={() => { setSearchLeilao(''); setForm({ ...form, leilao_id: '' }) }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {showLeilaoDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {leiloes
                    .filter(l => !searchLeilao || contem((l.nome || ''), searchLeilao.toLowerCase()))
                    .slice(0, 15)
                    .map(l => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => {
                          setForm({ ...form, leilao_id: String(l.id) })
                          setSearchLeilao(l.nome)
                          setShowLeilaoDropdown(false)
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0 ${
                          String(l.id) === form.leilao_id ? 'bg-blue-50 font-medium' : ''
                        }`}
                      >
                        {l.nome}
                      </button>
                    ))}
                  {leiloes.filter(l => !searchLeilao || contem((l.nome || ''), searchLeilao.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-400">Nenhum leilão encontrado</div>
                  )}
                </div>
              )}
            </div>
            {form.leilao_id && (
              <p className="text-xs text-green-600 mt-1">✓ Leilão selecionado</p>
            )}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Voltar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-500 rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
